import { axiosInstance } from '@/plugins/axios';
import moment from 'moment';
import { maxPauseHours } from '@/app.config';

export const moduleState = {
  vacationPauses: [],
  hasUnlimited: false,
  loading: false,
  hoursUsed: 0,
  hoursAvailable: 0,
  maxPauseHours,
};

export const getters = {
  hoursUsed(state) {
    let totalHours = 0;
    for (const pause of state.vacationPauses) {
      const end = pause.actualEndTime || pause.endTime;
      const start = pause.actualStartTime || pause.startTime;
      totalHours += moment.duration(Date.parse(end) - Date.parse(start)).asHours();
    }
    return Math.ceil(totalHours);
  },

  hoursLeft(state, { hoursUsed }) {
    return state.maxPauseHours - hoursUsed;
  },

  upcomingPauses(state) {
    return state.vacationPauses.filter(p => p.status === 'PENDING');
  },

  activePause(state) {
    return state.vacationPauses.find(p => p.status === 'ACTIVE') ?? null;
  },

  pastPauses(state) {
    const now = Date.now();
    return state.vacationPauses.filter(p => now > Date.parse(p.actualEndTime));
  },
};

export const actions = {
  async getVacationPauses({ commit, dispatch, rootState }) {
    commit('setLoading', true);

    const response = await axiosInstance
      .get(`/api/vacations/client/${rootState.client.client.id}`)
      .catch(err => err);

    if (response instanceof Error) {
      // TODO: Post back to server side log?
      commit('setLoading', false);
      dispatch('toastr/error', 'Failed to get scheduled vacations!', {
        root: true,
      });
      return;
    }

    commit('setVacationPauses', response.data.vacationPauses);
    commit('setHasUnlimited', response.data.hasUnlimited);
    commit('setHoursUsed', response.data.hoursSummary.hoursUsed);
    commit('setHoursAvailable', response.data.hoursSummary.hoursAvailable);
    commit('setLoading', false);
  },

  async resetVacationHours({ commit, dispatch, rootState }) {
    commit('setLoading', true);

    const response = await axiosInstance
      .post(`/api/vacations/${rootState.client.client.id}/reset`)
      .catch(err => err);

    if (response instanceof Error) {
      commit('setLoading', false);
      dispatch('toastr/error', 'Failed to replenish vacation hours!', {
        root: true,
      });
      return false;
    }

    commit('setVacationPauses', response.data.vacationPauses);
    commit('setHasUnlimited', response.data.hasUnlimited);
    commit('setHoursUsed', response.data.hoursSummary.hoursUsed);
    commit('setHoursAvailable', response.data.hoursSummary.hoursAvailable);

    commit('setLoading', false);
    return true;
  },

  async upsertVacationPause({ commit, dispatch, rootState }, vacationPause) {
    commit('setLoading', true);

    vacationPause.clientId = rootState.client.client.id;

    const method = vacationPause.id ? 'put' : 'post';

    const response = await axiosInstance[method]('/api/vacations', vacationPause).catch(err => err);

    if (response instanceof Error) {
      commit('setLoading', false);
      dispatch(
        'toastr/error',
        `Failed to create vacation pause. Please check the dates and that the new pause does not overlap any existing ones, and try again.`,
        {
          root: true,
        }
      );
      return;
    }

    if (method === 'put') {
      commit('updateVacationPause', vacationPause);
      commit('setLoading', false);
      return;
    }

    commit('client/SET_CLIENT_STATUS', response.data.clientStatus, {
      root: true,
    });
    commit('addVacationPause', response.data);
    commit('setLoading', false);
  },

  async cancelVacationPause({ commit, dispatch, state }, vacationPauseId) {
    commit('setLoading', true);

    const vacationPause = state.vacationPauses.find(v => v.id === vacationPauseId);

    if (vacationPause.status === 'ACTIVE') {
      commit('completeVacationPause', vacationPauseId);
    } else {
      commit('cancelVacationPause', vacationPauseId);
    }

    const response = await axiosInstance
      .delete(`/api/Vacations/${vacationPauseId}`, {
        data: vacationPause,
      })
      .catch(err => err);

    if (response instanceof Error) {
      commit('uncancelVacationPause', vacationPauseId);
      commit('setLoading', false);
      dispatch('toastr/error', `Failed to cancel vacation. Reason: ${response.response.data}`, {
        root: true,
      });
      return;
    }

    commit('client/SET_CLIENT_STATUS', response.data.clientStatus, {
      root: true,
    });
    commit('setLoading', false);
  },
};

export const mutations = {
  setVacationPauses(state, value) {
    state.vacationPauses = value;
  },
  setHasUnlimited(state, value) {
    state.hasUnlimited = value;
  },
  setLoading(state, value) {
    state.loading = value;
  },

  addVacationPause(state, vacationPause) {
    state.vacationPauses.push(vacationPause);

    // update used / remaining time
    const usedHours = moment
      .duration(Date.parse(vacationPause.endTime) - Date.parse(vacationPause.startTime))
      .asHours();

    state.hoursUsed = Math.round((state.hoursUsed + usedHours) * 100) / 100;
    state.hoursAvailable = Math.round((state.hoursAvailable - usedHours) * 100) / 100;
  },

  updateVacationPause(state, vacationPause) {
    // Figure out time used in updated vacation
    const usedHoursNew = moment
      .duration(Date.parse(vacationPause.endTime) - Date.parse(vacationPause.startTime))
      .asHours();

    for (let i = 0; i < state.vacationPauses.length; i++) {
      if (state.vacationPauses[i].id === vacationPause.id) {
        // Get current vacation time used

        const usedHoursCurrent = moment
          .duration(
            Date.parse(state.vacationPauses[i].endTime) -
              Date.parse(state.vacationPauses[i].startTime)
          )
          .asHours();

        // replace current time with new time
        if (usedHoursNew > usedHoursCurrent) {
          const increasedTime = usedHoursNew - usedHoursCurrent;

          state.hoursAvailable -= increasedTime;
          state.hoursUsed += increasedTime;
        } else {
          const decreasedTime = usedHoursCurrent - usedHoursNew;

          state.hoursAvailable += decreasedTime;
          state.hoursUsed -= decreasedTime;
        }

        state.vacationPauses[i] = vacationPause;
        return;
      }
    }
  },

  cancelVacationPause(state, vacationPauseId) {
    const pause = state.vacationPauses.find(v => v.id === vacationPauseId);
    pause.status = 'CANCELED';
  },

  completeVacationPause(state, vacationPauseId) {
    const pause = state.vacationPauses.find(v => v.id === vacationPauseId);
    pause.actualEndTime = moment().utc().toISOString();
    pause.status = 'COMPLETED';
  },

  uncancelVacationPause(state, vacationPauseId) {
    const pause = state.vacationPauses.find(v => v.id === vacationPauseId);
    pause.actualEndTime = undefined;
    pause.status = 'ACTIVE';
  },

  setHoursUsed(state, val) {
    state.hoursUsed = val;
  },
  setHoursAvailable(state, val) {
    state.hoursAvailable = val;
  },
};
